import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AlertBanner from '../../../components/common/AlertBanner/AlertBanner';
import Badge from '../../../components/common/Badge/Badge';
import Button from '../../../components/common/Button/Button';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import FiltersGroup from '../../../components/common/FiltersGroup/FiltersGroup';
import DurationPickerField from '../../../components/common/DurationPickerField/DurationPickerField';
import FormInput from '../../../components/common/FormInput/FormInput';
import FormSelect from '../../../components/common/FormSelect/FormSelect';
import FormTextarea from '../../../components/common/FormTextarea/FormTextarea';
import Icon from '../../../components/common/Icon/Icon';
import Loading from '../../../components/common/Loading/Loading';
import MultiSelectFilter from '../../../components/common/MultiSelectFilter/MultiSelectFilter';
import PageContainer from '../../../components/common/PageContainer/PageContainer';
import ProfileColumns from '../../../components/common/ProfileColumns/ProfileColumns';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import ContentContainer from '../../../components/common/ContentContainer/ContentContainer';
import StickyHeader from '../../../components/common/StickyHeader/StickyHeader';
import { BreadcrumbItem, useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import { useAuth } from '../../../contexts/AuthContext';
import useTrainingBuilder from '../../../hooks/useTrainingBuilder';
import { StudentTrainingService } from '../../../services/StudentTrainingService';
import type { ExerciseItem } from '../../../types/exercise';
import { TRAINING_CATEGORY_LABELS, isTabataSupportedTrainingGroupingType, type TrainingFormValues } from '../../../types/training';
import { isTabataTrainingExecutionMethod } from '../../../utils/trainingExecutionMethods';
import TrainingBlockEditor from '../components/TrainingBlockEditor';
import TrainingCatalogCard from '../components/TrainingCatalogCard/TrainingCatalogCard';
import ExerciseExecutionEditor from '../components/ExerciseExecutionEditor/ExerciseExecutionEditor';
import TrainingSummaryPanel from '../components/TrainingSummaryPanel/TrainingSummaryPanel';
import type { TrainingExerciseExecution } from '../../../types/training';
import { setTrainingDragData, setTrainingDragPreview } from '../utils/trainingDragData';
import { useTrainingExerciseMultiSelect } from '../hooks/useTrainingExerciseMultiSelect';
import { canManageTrainings } from '../../../utils/permissions';
import { getStudentTrainingsTabPath } from '../../../utils/studentTrainingList';
import styles from './TrainingRegister.module.css';

const findExerciseInTree = (
  items: TrainingExerciseNode[],
  exerciseId: string,
  inheritedStructureType: TrainingExerciseNode['groupingType'] = 'multiple_set',
  tabataController: { id: string; name: string } | null = null,
): ActiveExerciseContext | null => {
  for (const item of items) {
    const structureType = item.groupingType ?? inheritedStructureType ?? 'multiple_set';
    const hasGroupedExercises = (item.conjugatedExercises?.length ?? 0) > 0;
    const isTabataMethod = isTabataTrainingExecutionMethod(item.execution.method);
    const isTabataGroup = isTabataSupportedTrainingGroupingType(structureType)
      && hasGroupedExercises
      && isTabataMethod;
    const isStandaloneTabata = structureType === 'multiple_set' && !hasGroupedExercises && isTabataMethod;
    const nextTabataController = (isTabataGroup || isStandaloneTabata)
      ? { id: item.id, name: item.name }
      : tabataController;

    if (item.id === exerciseId) {
      return {
        exercise: item,
        structureType,
        isTabataController: nextTabataController?.id === item.id,
        isTabataReadOnly: Boolean(nextTabataController && nextTabataController.id !== item.id),
        tabataControllerName: nextTabataController?.id !== item.id ? nextTabataController?.name ?? null : null,
      };
    }

    const nestedMatch = findExerciseInTree(item.conjugatedExercises ?? [], exerciseId, structureType, nextTabataController);

    if (nestedMatch) {
      return nestedMatch;
    }
  }

  return null;
};

const countExercisesInTree = (items: TrainingExerciseNode[]): number => items.reduce(
  (total, item) => total + 1 + countExercisesInTree(item.conjugatedExercises ?? []),
  0,
);

type TrainingExerciseNode = NonNullable<ReturnType<typeof useTrainingBuilder>['blocks']>[number]['items'][number];

interface ActiveExerciseContext {
  exercise: TrainingExerciseNode;
  structureType: TrainingExerciseNode['groupingType'] | 'multiple_set';
  isTabataController: boolean;
  isTabataReadOnly: boolean;
  tabataControllerName: string | null;
}

export interface TrainingRegisterProps {
  studentId?: string;
  studentTrainingId?: string;
  onClose?: () => void;
}

const TrainingRegister: React.FC<TrainingRegisterProps> = ({
  studentId: studentIdProp,
  studentTrainingId: studentTrainingIdProp,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useAuth();
  const canManage = canManageTrainings(roles);
  const isReadOnly = !canManage;
  const {
    trainingId,
    studentId: routeStudentId,
    studentTrainingId: routeStudentTrainingId,
  } = useParams<{
    trainingId: string;
    studentId: string;
    studentTrainingId: string;
  }>();
  const studentId = studentIdProp ?? routeStudentId;
  const studentTrainingId = studentTrainingIdProp ?? routeStudentTrainingId;
  const isEmbedded = Boolean(onClose);
  const isStudentTraining = Boolean(studentId && studentTrainingId);
  const resolvedTrainingId = isStudentTraining ? studentTrainingId : trainingId;
  const studentTrainingStorage = useMemo(
    () => (studentId ? StudentTrainingService.createStorage(studentId) : undefined),
    [studentId],
  );
  const { setBreadcrumbs } = useBreadcrumbs();
  const {
    mode,
    formValues,
    errors,
    blocks,
    submitError,
    isSaving,
    isLoadingTraining,
    isMissingTraining,
    catalogExercises,
    catalogLoading,
    catalogSearchTerm,
    catalogGroups,
    catalogGroupOptions,
    hasActiveCatalogFilters,
    selectedExercise,
    isSummaryLoading,
    summaryError,
    summaryMetrics,
    muscleVolumes,
    blockSummaryMetricsById,
    activeExerciseConfig,
    executionMethodOptions,
    categoryOptions,
    levelOptions,
    objectiveOptions,
    monthOptions,
    locationOptions,
    frequencyOptions,
    handleFieldChange,
    handleCatalogSearchChange,
    handleCatalogGroupsChange,
    handleClearCatalogFilters,
    handleSelectExercise,
    handleAddExerciseToBlock,
    handleDropCatalogExerciseToBlock,
    handleOpenExerciseConfig,
    handleCloseExerciseConfig,
    handleUpdateExerciseExecution,
    handleRemoveExerciseFromBlock,
    handleReplaceExercise,
    handleMoveExerciseByDrag,
    handleMoveBlock,
    handleMoveBlockByDrag,
    handleMoveBlockToLabel,
    handleRemoveBlock,
    handleAddBlock,
    handleSave,
  } = useTrainingBuilder(resolvedTrainingId, studentTrainingStorage);
  const [isCatalogCollapsed, setIsCatalogCollapsed] = useState(false);
  const totalExerciseCount = useMemo(
    () => blocks.reduce((total, block) => total + countExercisesInTree(block.items), 0),
    [blocks],
  );
  const [isBasicInfoCollapsed, setIsBasicInfoCollapsed] = useState(false);
  const [executionAnchorRect, setExecutionAnchorRect] = useState<DOMRect | null>(null);
  const selectModeEndRef = useRef<(selected: Array<{ blockId: string; exerciseId: string }>) => void>(() => undefined);
  const {
    isSelectMode,
    selectedTargets,
    lastAnchorRectRef,
    toggleTarget,
    clearSelection,
  } = useTrainingExerciseMultiSelect(!isReadOnly, (selected) => {
    selectModeEndRef.current(selected);
  }, Boolean(executionAnchorRect));

  useEffect(() => {
    if (isEmbedded) {
      return undefined;
    }

    const breadcrumbs: BreadcrumbItem[] = [
      ...(isStudentTraining && studentId
        ? [{ label: 'Treinos da aluna', path: getStudentTrainingsTabPath(studentId) }]
        : [{ label: 'Treinos', path: '/treinos' }]),
      { label: isStudentTraining ? 'Editar treino da aluna' : mode === 'edit' ? 'Editar treino' : 'Novo treino' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => setBreadcrumbs([]);
  }, [isEmbedded, isStudentTraining, mode, setBreadcrumbs, studentId]);

  const handleNavigateBack = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }

    navigate(isStudentTraining && studentId ? getStudentTrainingsTabPath(studentId) : '/treinos');
  }, [isStudentTraining, navigate, onClose, studentId]);

  const handleFormChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    handleFieldChange(event.target.name as keyof TrainingFormValues, event.target.value);
  }, [handleFieldChange]);

  const handleObjectiveChange = useCallback((values: string[]) => {
    handleFieldChange('objective', values);
  }, [handleFieldChange]);

  const handleLevelsChange = useCallback((values: string[]) => {
    handleFieldChange('levels', values);
  }, [handleFieldChange]);

  const handleTrainingDurationChange = useCallback((value: string) => {
    const totalSeconds = Number.parseInt(value, 10);

    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
      handleFieldChange('duration', '');
      return;
    }

    handleFieldChange('duration', String(Math.floor(totalSeconds / 60)));
  }, [handleFieldChange]);

  const handlePersistTraining = useCallback(async () => {
    const savedTraining = await handleSave();

    if (!savedTraining) {
      return;
    }

    if (onClose) {
      return;
    }

    const nextPath = isStudentTraining && studentId
      ? `/alunas/${studentId}/treino/${savedTraining.id}`
      : `/treinos/${savedTraining.id}/editar`;

    window.location.href = `${nextPath}${location.search}`;
  }, [handleSave, isStudentTraining, location.search, onClose, studentId]);

  const handleCardOpenConfig = useCallback(async (blockId: string, exerciseId: string, anchorRect: DOMRect) => {
    await handleOpenExerciseConfig(blockId, exerciseId);
    setExecutionAnchorRect(anchorRect);
  }, [handleOpenExerciseConfig]);

  const handleCloseExecution = useCallback(() => {
    handleCloseExerciseConfig();
    setExecutionAnchorRect(null);
    clearSelection();
  }, [clearSelection, handleCloseExerciseConfig]);

  const handleConfirmExecution = useCallback((execution: TrainingExerciseExecution) => {
    const targets = selectedTargets.length > 0
      ? selectedTargets
      : (activeExerciseConfig ? [activeExerciseConfig] : []);

    targets.forEach((target) => {
      handleUpdateExerciseExecution(target.blockId, target.exerciseId, execution);
    });

    handleCloseExerciseConfig();
    setExecutionAnchorRect(null);
    clearSelection();
  }, [activeExerciseConfig, clearSelection, handleCloseExerciseConfig, handleUpdateExerciseExecution, selectedTargets]);

  selectModeEndRef.current = (selected) => {
    if (selected.length === 0 || executionAnchorRect) {
      return;
    }

    const writableTarget = [...selected].reverse().find((target) => {
      const context = findExerciseInTree(
        blocks.find((block) => block.id === target.blockId)?.items ?? [],
        target.exerciseId,
      );

      return Boolean(context && !context.isTabataReadOnly);
    }) ?? selected[selected.length - 1];

    void handleOpenExerciseConfig(writableTarget.blockId, writableTarget.exerciseId).then(() => {
      setExecutionAnchorRect(lastAnchorRectRef.current ?? new DOMRect(window.innerWidth / 2, 140, 0, 0));
    });
  };

  const activeExerciseContext = activeExerciseConfig
    ? findExerciseInTree(
      blocks.find((block) => block.id === activeExerciseConfig.blockId)?.items ?? [],
      activeExerciseConfig.exerciseId,
    )
    : null;
  const activeExercise = activeExerciseContext?.exercise ?? null;
  const activeExerciseStructureType = activeExerciseContext?.structureType ?? 'multiple_set';
  const selectedHeaderExercises = useMemo(() => {
    const targets = selectedTargets.length > 0
      ? selectedTargets
      : (activeExerciseConfig ? [activeExerciseConfig] : []);

    return targets.flatMap((target) => {
      const context = findExerciseInTree(
        blocks.find((block) => block.id === target.blockId)?.items ?? [],
        target.exerciseId,
      );

      if (!context?.exercise) {
        return [];
      }

      return [{
        id: context.exercise.id,
        blockId: target.blockId,
        catalogExerciseId: context.exercise.exerciseId,
        name: context.exercise.name,
      }];
    });
  }, [activeExerciseConfig, blocks, selectedTargets]);
  const trainingDurationPickerValue = (() => {
    const durationMinutes = Number.parseInt(formValues.duration, 10);

    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      return '';
    }

    return String(durationMinutes * 60);
  })();

  const handleCatalogExerciseDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>, exercise: ExerciseItem) => {
    handleSelectExercise(exercise);
    setTrainingDragData(event, {
      type: 'catalog-exercise',
      exercise,
    });
    setTrainingDragPreview(event, event.currentTarget, { onlyImage: true });
  }, [handleSelectExercise]);

  return (
    <PageContainer className={styles.container}>
      <StickyHeader
        className={isEmbedded ? styles.embeddedHeader : undefined}
        contentClassName={styles.headerContent}
        topBarClassName={styles.topBar}
        actionsClassName={styles.topBarRight}
        backButtonClassName={styles.backButton}
        saveButtonClassName={styles.saveButton}
        onBack={handleNavigateBack}
        onCancel={handleNavigateBack}
        onSave={canManage ? handlePersistTraining : undefined}
        isSaving={isSaving}
        disabled={isReadOnly || isSaving || isLoadingTraining || isMissingTraining}
      />

      {submitError && <AlertBanner message={submitError} variant="error" className={styles.alert} />}
      {/* {isReadOnly && <AlertBanner message="Seu perfil possui acesso somente leitura. Apenas administradoras podem criar, editar ou excluir treinos." variant="warning" className={styles.alert} />} */}

      <ContentContainer className={styles.contentBg}>
        {isLoadingTraining ? (
          <Loading />
        ) : isMissingTraining ? (
          <EmptyState message="Treino não encontrado." />
        ) : (
        <ProfileColumns className={`${styles.columns}${isReadOnly ? ` ${styles.columnsReadOnly}` : ''}${!isReadOnly && isCatalogCollapsed ? ` ${styles.columnsCatalogCollapsed}` : ''}`}>
          {!isReadOnly && (
          <aside className={`${styles.catalogColumn}${isCatalogCollapsed ? ` ${styles.catalogColumnCollapsed}` : ''}`}>
            <section className={`${styles.panel} ${styles.basicInfoPanel}`}>
              <div className={`${styles.panelHeader} ${styles.catalogHeader}${isCatalogCollapsed ? ` ${styles.catalogHeaderCollapsed}` : ''}`}>
                {!isCatalogCollapsed && (
                  <span className={styles.catalogHeaderText}>
                    Catálogo de Exercícios
                  </span>
                )}
                <button
                  type="button"
                  className={styles.catalogToggleButton}
                  onClick={() => setIsCatalogCollapsed((currentValue) => !currentValue)}
                  aria-expanded={!isCatalogCollapsed}
                  aria-label={isCatalogCollapsed ? 'Mostrar catálogo de exercícios' : 'Ocultar catálogo de exercícios'}
                >
                  <Icon
                    name="left_panel_close"
                    size={16}
                    className={`${styles.catalogHeaderIcon}${isCatalogCollapsed ? ` ${styles.catalogHeaderIconCollapsed}` : ''}`}
                  />
                </button>
              </div>

              <div className={`${styles.catalogBody}${isCatalogCollapsed ? ` ${styles.catalogBodyCollapsed}` : ''}`}>
                <FiltersGroup className={styles.catalogFilters}>
                  <SearchFilterBar
                    searchValue={catalogSearchTerm}
                    onSearchChange={handleCatalogSearchChange}
                    searchPlaceholder="Pesquisar"
                    loading={catalogLoading}
                    className={styles.catalogSearchBar}
                    searchWrapperClassName={styles.catalogSearchWrapper}
                    inputClassName={styles.catalogSearchInput}
                  />

                  <MultiSelectFilter
                    values={catalogGroups}
                    onChange={handleCatalogGroupsChange}
                    options={catalogGroupOptions}
                    placeholder="Grupo"
                    clearButtonLabel="Limpar filtro"
                    hideChips
                    showSelectionCount={false}
                    className={styles.groupFilter}
                    wrapperClassName={styles.groupFilterWrapper}
                    dropdownClassName={styles.groupFilterDropdown}
                  />

                  <Button
                    variant="ghost"
                    size="small"
                    icon="filter_alt"
                    className={styles.filterButton}
                    onClick={handleClearCatalogFilters}
                    disabled={!hasActiveCatalogFilters}
                    aria-label="Limpar filtros"
                    title={hasActiveCatalogFilters ? 'Limpar filtros' : 'Nenhum filtro ativo'}
                    type="button"
                  />
                </FiltersGroup>

                {catalogLoading ? (
                  <div className={styles.catalogBodyLoading}>
                    <Loading inline />
                  </div>
                ) : (
                  <div className={styles.catalogList}>
                    {catalogExercises.length === 0 ? (
                    <div className={styles.catalogEmptyState}>
                      <EmptyState message="Nenhum exercício encontrado com os filtros selecionados." />
                    </div>
                    ) : (
                      catalogExercises.map((exercise) => (
                        <TrainingCatalogCard
                          key={exercise.id}
                          exercise={exercise}
                          isSelected={selectedExercise?.id === exercise.id}
                          onSelect={handleSelectExercise}
                          onDragStart={handleCatalogExerciseDragStart}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </section>
          </aside>
          )}

          <div className={styles.mainColumn}>
            <section className={`${styles.panel} ${styles.basicInfoPanel}`}>
              <div className={styles.panelHeaderRow}>
                <span className={styles.panelTitle}>Informações Básicas</span>

                <div className={styles.panelHeaderActions}>
                  <div className={styles.categorySummary}>
                    <span className={styles.categorySummaryLabel}>Categoria:</span>
                    {formValues.category ? (
                      <Badge variant="info" className={styles.categoryBadge}>
                        {TRAINING_CATEGORY_LABELS[formValues.category]}
                      </Badge>
                    ) : (
                      <span className={styles.categorySummaryValue}>Selecionar</span>
                    )}
                  </div>

                  <button
                    type="button"
                    className={styles.panelToggleButton}
                    onClick={() => setIsBasicInfoCollapsed((currentValue) => !currentValue)}
                    aria-expanded={!isBasicInfoCollapsed}
                    aria-controls="training-basic-info-panel"
                    aria-label={isBasicInfoCollapsed ? 'Expandir informações básicas' : 'Ocultar informações básicas'}
                  >
                    <Icon
                      name="expand_more"
                      size={18}
                      className={`${styles.panelToggleIcon}${!isBasicInfoCollapsed ? ` ${styles.panelToggleIconExpanded}` : ''}`}
                    />
                  </button>
                </div>
              </div>

              <div
                id="training-basic-info-panel"
                className={`${styles.collapsiblePanelBody}${isBasicInfoCollapsed ? ` ${styles.collapsiblePanelBodyCollapsed}` : ''}`}
              >
                <div className={styles.collapsiblePanelBodyInner}>
                  <div className={`${styles.panelBody} ${styles.collapsiblePanelContent}`}>
                    <div className={styles.categoryFieldRow}>
                      <div className={styles.nameField}>
                        <FormInput
                          id="training-name"
                          name="name"
                          label="Nome do treino"
                          required
                          value={formValues.name}
                          onChange={handleFormChange}
                          placeholder="Nome treino lorem ipsum"
                          error={errors.name}
                          readOnly={isReadOnly}
                        />
                      </div>

                      <FormSelect
                        id="training-category"
                        name="category"
                        label="Categoria"
                        value={formValues.category}
                        onChange={handleFormChange}
                        options={categoryOptions}
                        placeholder="Selecionar"
                        error={errors.category}
                        containerClassName={styles.categoryField}
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className={styles.primaryFieldsGrid}>
                      <div className={styles.multiSelectFieldGroup}>
                        <label htmlFor="training-level" className={styles.multiSelectLabel}>
                          Nível
                          <span className={styles.multiSelectRequired}>*</span>
                        </label>
                        <MultiSelectFilter
                          values={formValues.levels}
                          onChange={handleLevelsChange}
                          options={levelOptions}
                          placeholder="Selecionar níveis"
                          className={styles.objectiveMultiSelectTrigger}
                          wrapperClassName={styles.objectiveMultiSelectWrapper}
                          dropdownClassName={styles.objectiveMultiSelectDropdown}
                          hideChips
                          chipsInTrigger
                          showSelectionCount={false}
                          disabled={isReadOnly}
                        />
                        {errors.levels && <span className={styles.multiSelectError}>{errors.levels}</span>}
                      </div>

                      <div className={styles.multiSelectFieldGroup}>
                        <label htmlFor="training-objective" className={styles.multiSelectLabel}>
                          Objetivo
                          <span className={styles.multiSelectRequired}>*</span>
                        </label>
                        <MultiSelectFilter
                          values={formValues.objective}
                          onChange={handleObjectiveChange}
                          options={objectiveOptions}
                          placeholder="Selecionar objetivos"
                          className={styles.objectiveMultiSelectTrigger}
                          wrapperClassName={styles.objectiveMultiSelectWrapper}
                          dropdownClassName={styles.objectiveMultiSelectDropdown}
                          hideChips
                          chipsInTrigger
                          showSelectionCount={false}
                          disabled={isReadOnly}
                        />
                        {errors.objective && <span className={styles.multiSelectError}>{errors.objective}</span>}
                      </div>

                      <FormSelect
                        id="training-month"
                        name="month"
                        label="Mês"
                        value={formValues.month}
                        onChange={handleFormChange}
                        options={monthOptions}
                        placeholder="Selecionar"
                        error={errors.month}
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className={styles.secondaryFieldsGrid}>
                      <div className={styles.descriptionField}>
                        <FormTextarea
                          id="training-description"
                          name="description"
                          label="Descrição do treino"
                          value={formValues.description}
                          onChange={handleFormChange}
                          placeholder="O treino de hipertrofia visa o aumento do volume e da massa muscular através de exercícios com sobrecarga progressiva, geralmente realizados em academias."
                          rows={4}
                          error={errors.description}
                          readOnly={isReadOnly}
                        />
                      </div>

                      <FormSelect
                        id="training-location"
                        name="location"
                        label="Local"
                        value={formValues.location}
                        onChange={handleFormChange}
                        options={locationOptions}
                        placeholder="Selecionar"
                        error={errors.location}
                        disabled={isReadOnly}
                      />

                      <FormSelect
                        id="training-frequency"
                        name="frequency"
                        label="Frequência semanal"
                        value={formValues.frequency}
                        onChange={handleFormChange}
                        options={frequencyOptions}
                        placeholder="Selecionar"
                        error={errors.frequency}
                        disabled={isReadOnly}
                      />

                      <DurationPickerField
                        id="training-duration"
                        label="Tempo de treino"
                        value={trainingDurationPickerValue}
                        onChange={handleTrainingDurationChange}
                        placeholder="Tempo do treino"
                        error={errors.duration}
                        maxMinutes={180}
                        pickerMode="hours-minutes"
                        dialogTitle="Configurar tempo do treino"
                        dialogSubtitle=""
                        emptyValueText="Sem duração"
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className={`${styles.panel} ${styles.exercisePanel}`}>
              <div className={`${styles.panelHeader} ${styles.exercisePanelHeader}`}>
                <div className={styles.exercisePanelLayoutGrid}>
                  <div className={styles.exercisePanelLayoutSpacer} aria-hidden="true" />
                  <div className={styles.exercisePanelHeaderShell}>
                    <span className={styles.exercisePanelHeaderTitle}>Exercícios</span>
                  </div>
                  <div className={styles.exercisePanelLayoutSpacer} aria-hidden="true" />
                </div>
              </div>

              <div className={`${styles.panelBody} ${styles.exercisePanelBody}${totalExerciseCount > 3 ? ` ${styles.exercisePanelBodyExpanded}` : ''}`}>
                <div className={styles.blocksList}>
                  {blocks.map((block) => (
                    <TrainingBlockEditor
                      key={block.id}
                      block={block}
                      summaryMetrics={blockSummaryMetricsById[block.id] ?? []}
                      methodOptions={executionMethodOptions}
                      selectedExercise={selectedExercise}
                      activeExerciseId={activeExerciseConfig?.exerciseId ?? null}
                      canRemoveBlock={blocks.length > 1}
                      canMoveBlockUp={blocks.findIndex((item) => item.id === block.id) > 0}
                      canMoveBlockDown={blocks.findIndex((item) => item.id === block.id) < blocks.length - 1}
                      onAddExercise={handleAddExerciseToBlock}
                      onOpenExerciseConfig={handleCardOpenConfig}
                      onRemoveExercise={handleRemoveExerciseFromBlock}
                      onReplaceExercise={handleReplaceExercise}
                      onDropCatalogExercise={handleDropCatalogExerciseToBlock}
                      onMoveExerciseByDrag={handleMoveExerciseByDrag}
                      onMoveBlockUp={(blockId) => handleMoveBlock(blockId, 'up')}
                      onMoveBlockDown={(blockId) => handleMoveBlock(blockId, 'down')}
                      onMoveBlockByDrag={(sourceBlockId, targetBlockId) => handleMoveBlockByDrag(sourceBlockId, targetBlockId)}
                      onMoveBlockToLabel={handleMoveBlockToLabel}
                      splitLabels={blocks.map((item) => item.label)}
                      onRemoveBlock={handleRemoveBlock}
                      readOnly={isReadOnly}
                      isMultiSelectMode={isSelectMode}
                      isSelectionLocked={Boolean(executionAnchorRect)}
                      selectedExerciseIds={selectedTargets.map((target) => target.exerciseId)}
                      onToggleExerciseSelection={toggleTarget}
                    />
                  ))}
                </div>

                {errors.blocks && <span className={styles.inlineError}>{errors.blocks}</span>}

                {!isReadOnly && (
                  <div className={styles.exerciseAddBlockRow}>
                    <div className={styles.exercisePanelLayoutSpacer} aria-hidden="true" />
                    <Button
                      variant="ghost"
                      size="small"
                      className={`${styles.addBlockButton} ${styles.exerciseAddBlockButton}`}
                      onClick={handleAddBlock}
                      type="button"
                    >
                      Adicionar
                    </Button>
                    <div className={styles.exercisePanelLayoutSpacer} aria-hidden="true" />
                  </div>
                )}
              </div>
            </section>

            <section className={styles.panel}>
              <div className={styles.panelHeader}>Resumo do treino</div>

              <div className={styles.panelBody}>
                <TrainingSummaryPanel
                  metrics={summaryMetrics}
                  muscleVolumes={muscleVolumes}
                  isLoading={isSummaryLoading}
                  errorMessage={summaryError}
                />
              </div>
            </section>
          </div>
        </ProfileColumns>
        )}
      </ContentContainer>

      {activeExercise && executionAnchorRect && !isReadOnly && (
        <ExerciseExecutionEditor
          execution={activeExercise.execution}
          exerciseName={activeExercise.name}
          catalogExerciseId={activeExercise.exerciseId}
          structureType={activeExerciseStructureType}
          anchorRect={executionAnchorRect}
          methodOptions={executionMethodOptions}
          isReadOnly={activeExerciseContext?.isTabataReadOnly}
          isTabataController={activeExerciseContext?.isTabataController}
          tabataControllerName={activeExerciseContext?.tabataControllerName}
          selectedExercises={selectedHeaderExercises}
          onConfirm={handleConfirmExecution}
          onClose={handleCloseExecution}
          onReplaceExercise={(nextExercise, replaceInEntirePlan, target) => {
            const blockId = target.blockId || activeExerciseConfig?.blockId;
            const exerciseId = target.exerciseId || activeExerciseConfig?.exerciseId;

            if (!blockId || !exerciseId) {
              return;
            }

            handleReplaceExercise(blockId, exerciseId, nextExercise, replaceInEntirePlan);
          }}
        />
      )}
    </PageContainer>
  );
};

export default TrainingRegister;
